import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { btnCls, cget, cpost, downloadTide, errText, gbp, openPdf } from "./api";

export const LedgerTab = ({ ym }) => {
  const [rows, setRows] = useState([]);
  const load = () => cget("/ledger", { ym }).then((d) => setRows(d.entries)).catch(() => {});
  useEffect(() => { load(); }, [ym]); // eslint-disable-line react-hooks/exhaustive-deps
  const reverse = (e, reason) => {
    const amt = window.prompt(`${reason === "refund" ? "Refund" : "Chargeback"} amount (£, up to ${e.gross})`, String(e.gross));
    if (!amt) return;
    cpost("/reversal", { ledger_id: e.id, amount: Number(amt), reason }).then(() => { toast.success("Reversal recorded"); load(); }).catch((x) => toast.error(errText(x)));
  };
  return (
    <div className="overflow-x-auto" data-testid="commissions-ledger-tab">
      <table className="w-full min-w-[1150px] text-right text-xs">
        <thead className="uppercase tracking-wider text-[#6b6b6b]"><tr><th className="py-2 text-left">Date</th><th className="text-left">Customer</th><th className="text-left">Product</th><th>Gross</th><th>VAT</th><th>Net</th><th>Affiliate</th><th>Partner</th><th>Principals</th><th>Processing</th><th>Costs</th><th>Contribution</th><th /></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className={`border-t border-[#eee9dc] ${e.kind === "reversal" ? "text-[#a0522d]" : ""}`} data-testid={`ledger-row-${e.id}`}>
              <td className="py-1.5 text-left">{e.paid_at?.slice(0, 10)}</td>
              <td className="text-left">{e.customer_email || e.customer_id}</td>
              <td className="text-left">{e.product}{e.kind === "reversal" ? ` · ${e.reason}` : ""}{e.tax_source === "fallback" ? " · VAT est." : ""}</td>
              <td>{gbp(e.gross)}</td><td>{gbp(e.tax)}</td><td>{gbp(e.net)}</td>
              <td>{gbp(e.affiliate.amount)}</td><td>{gbp(e.partner.amount)}</td><td>{gbp(e.principal_total)}</td>
              <td>{gbp(e.processing)}</td><td>{gbp(e.operating_total)}</td><td className="font-medium">{gbp(e.contribution)}</td>
              <td className="whitespace-nowrap pl-2">{e.kind === "sale" && <>
                <button type="button" className="underline" onClick={() => reverse(e, "refund")} data-testid={`ledger-refund-${e.id}`}>Refund</button>{" · "}
                <button type="button" className="underline" onClick={() => reverse(e, "chargeback")}>Chargeback</button></>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="mt-3 text-sm text-[#8a8a8a]" data-testid="ledger-empty">No ledger entries for this month.</p>}
      <p className="mt-3 text-xs text-[#8a8a8a]">Refunds and chargebacks from Stripe are added here automatically as reversal lines. The original payment is never changed.</p>
    </div>
  );
};

const StatementRow = ({ s, onPaid }) => {
  const paid = () => {
    const ref = window.prompt("Bank transfer reference");
    if (!ref) return;
    cpost(`/payouts/${s.payout.id}/paid`, { reference: ref, paid_on: new Date().toISOString().slice(0, 10) })
      .then((r) => { toast.success(r?.emailed ? "Marked as paid · statement emailed" : "Marked as paid · no email sent (no address on file)"); onPaid(); }).catch((e) => toast.error(errText(e)));
  };
  const approve = () => cpost(`/payouts/${s.payout.id}/approve`).then(() => { toast.success("Approved for payment"); onPaid(); }).catch((e) => toast.error(errText(e)));
  return (
    <tr className="border-t border-[#eee9dc]" data-testid={`statement-row-${s.id}`}>
      <td className="py-2 text-left">{s.name}<div className="text-[#8a8a8a]">{s.roles.join(", ")} · {s.email || ""}</div></td>
      <td>{gbp(s.opening)}</td><td>{gbp(s.earned)}</td><td>{gbp(s.reversed)}</td><td>{gbp(s.closing)}</td>
      <td>{s.vat?.treatment === "add_vat" ? gbp(s.vat.vat) : s.vat?.treatment === "reverse_charge" ? "reverse charge" : "–"}</td>
      <td className="font-medium">{s.payable ? gbp(s.total_to_pay) : `carried ${gbp(s.carried_forward)}`}</td>
      <td className="text-left">{s.payout ? (s.payout.status === "paid" ? `Paid ${s.payout.paid_on} · ${s.payout.reference}` : (
        <span className="inline-flex items-center gap-2">
          {s.payout.status === "due" ? (
            <button type="button" className={btnCls} onClick={approve} data-testid={`statement-approve-${s.id}`}>Approve</button>
          ) : <span className="text-[#3f6b46]" data-testid={`statement-approved-${s.id}`}>Approved</span>}
          <button type="button" className="underline" onClick={paid} data-testid={`statement-mark-paid-${s.id}`}>Mark paid</button>
        </span>)) : "–"}</td>
      <td><button type="button" className="underline" onClick={() => openPdf(`/admin/statements/${s.id}/pdf`)} data-testid={`statement-pdf-${s.id}`}>{s.invoice_no || "Statement"} PDF</button></td>
    </tr>
  );
};

export const StatementsTab = ({ ym }) => {
  const [rows, setRows] = useState([]);
  const load = () => cget("/statements", { ym }).then((d) => setRows(d.statements)).catch(() => {});
  useEffect(() => { load(); }, [ym]); // eslint-disable-line react-hooks/exhaustive-deps
  const gen = () => cpost("/statements/generate", { ym }).then((d) => { toast.success(`${d.created.length} statements created`); load(); }).catch((e) => toast.error(errText(e)));
  const tide = () => downloadTide(ym).then((n) => {
    if (n) toast.warning(`${n} approved payee(s) left out: no UK bank details on file`);
    else toast.success("Tide file downloaded");
  }).catch(() => toast.error("Couldn't create the Tide file"));
  return (
    <div data-testid="commissions-statements-tab">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnCls} onClick={gen} data-testid="statements-generate-btn">Create statements for {ym}</button>
        <button type="button" className={btnCls} onClick={tide} data-testid="statements-tide-export-btn">Download Tide payment file</button>
      </div>
      <p className="mt-2 text-xs text-[#6b6b6b]">One per person owed money. Balances under the minimum payout roll forward. VAT-registered people get a numbered self-billing VAT invoice. Only approved payouts go in the Tide file; nothing is sent until you upload it in Tide and mark each one paid.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[1100px] text-right text-xs [&_td]:px-2 [&_th]:px-2">
          <thead className="uppercase tracking-wider text-[#6b6b6b]"><tr><th className="py-2 text-left">Payee</th><th>Brought fwd</th><th>Earned</th><th>Reversed</th><th>Balance</th><th>VAT</th><th>To pay</th><th className="text-left">Payout</th><th /></tr></thead>
          <tbody>{rows.map((s) => <StatementRow key={s.id} s={s} onPaid={load} />)}</tbody>
        </table>
        {!rows.length && <p className="mt-3 text-sm text-[#8a8a8a]" data-testid="statements-empty">No statements for this month yet.</p>}
      </div>
    </div>
  );
};
